import { useState, useEffect } from 'react'
import { getUserRole, UserRole, UserRoleResponse } from '../services/api'

interface UserRoleState {
  role: UserRole | null
  email: string | null
  displayName: string | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Fetch user display info from the database.
 *
 * NOTE: Authorization is determined by Cognito groups (via useAuth),
 * not by the database role. This hook is for display purposes only.
 */
export function useUserRole(): UserRoleState {
  const [role, setRole] = useState<UserRole | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [displayName, setDisplayName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function fetchRole() {
    setIsLoading(true)
    setError(null)
    try {
      const data: UserRoleResponse = await getUserRole()
      setRole(data.role)
      setEmail(data.email)
      setDisplayName(data.display_name)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch user role'
      setError(message)
      setRole(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchRole()
  }, [])

  return {
    role,
    email,
    displayName,
    isLoading,
    error,
    refetch: fetchRole,
  }
}
